<template>
    <app-modal ref="modal" :width="420">
        <template #header>
            <h3>Print / Export to {{ displayableFormat }}</h3>
        </template>

        <template #body>
            <div :class="theme.body">
                <h3>Choose Format</h3>

                <div class="row" :class="[theme.section, theme.format]">
                    <div class="col-md-4">
                        <div
                            :class="{
                                [theme.option]: true,
                                [theme.selected]: format === 'sheet',
                            }"
                            @click="format = 'sheet'"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 48 48"
                                width="42px"
                                height="42px"
                            >
                                <path
                                    fill="#43a047"
                                    d="M37,45H11c-1.657,0-3-1.343-3-3V6c0-1.657,1.343-3,3-3h19l10,10v29C40,43.657,38.657,45,37,45z"
                                />
                                <path fill="#c8e6c9" d="M40 13L30 13 30 3z" />
                                <path fill="#2e7d32" d="M30 13L40 23 40 13z" />
                                <path
                                    fill="#e8f5e9"
                                    d="M31,23H17h-2v2v2v2v2v2v2v2h18v-2v-2v-2v-2v-2v-2v-2H31z M17,25h4v2h-4V25z M17,29h4v2h-4V29z M17,33h4v2h-4V33z M31,35h-8v-2h8V35z M31,31h-8v-2h8V31z M31,27h-8v-2h8V27z"
                                />
                            </svg>
                        </div>
                    </div>
                    <div class="col-md-4">
                        <div
                            :class="{
                                [theme.option]: true,
                                [theme.selected]: format === 'pdf',
                            }"
                            @click="format = 'pdf'"
                        >
                            <i class="icon-file-pdf-color" title="PDF"></i>
                        </div>
                    </div>
                    <div class="col-md-4">
                        <div
                            :class="{
                                [theme.option]: true,
                                [theme.selected]: format === 'doc',
                            }"
                            @click="format = 'doc'"
                        >
                            <i
                                class="icon-file-document-color"
                                title="Google Doc"
                            ></i>
                        </div>
                    </div>
                </div>

                <template v-if="format !== 'sheet'">
                    <h3>Choose Orientation</h3>

                    <div
                        class="row"
                        :class="[theme.section, theme.orientation]"
                    >
                        <div class="col-md-6">
                            <div
                                :class="{
                                    [theme.option]: true,
                                    [theme.selected]:
                                        orientation === 'landscape',
                                }"
                                @click="orientation = 'landscape'"
                            >
                                Landscape
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div
                                :class="{
                                    [theme.option]: true,
                                    [theme.selected]:
                                        orientation === 'portrait',
                                }"
                                @click="orientation = 'portrait'"
                            >
                                Portrait
                            </div>
                        </div>
                    </div>
                </template>

                <h3>Remove / Re-order Columns</h3>

                <ul ref="cols">
                    <li
                        v-for="(col, index) in cols"
                        :key="`print-col-${index}`"
                        :data-name="col.name"
                        :class="{
                            [theme.inactive]: !col.checked,
                        }"
                    >
                        <input v-model="col.checked" type="checkbox" />
                        {{ col.label }}
                    </li>
                </ul>
            </div>
        </template>
        <template #footer>
            <button
                ref="printButton"
                class="btn btn-primary"
                data-verb="Printing..."
                @click="print"
            >
                Print
            </button>
        </template>
    </app-modal>
</template>

<script>
import { Modal } from 'larasnap/vue';
import Common from './mixins/Common.vue';
import MakesHttpRequests from './mixins/MakesHttpRequests.vue';
import { Confirm, Message } from 'larasnap/support';

export default {
    components: {
        'app-modal': Modal,
    },
    mixins: [Common, MakesHttpRequests],
    props: {
        data: Object,
    },
    data() {
        return {
            cols: this.data.allCols.map(col => ({
                checked: true,
                ...col,
            })),
            format: 'pdf',
            orientation: 'landscape',
        };
    },
    computed: {
        displayableFormat() {
            if (this.format === 'sheet') {
                return 'Google Sheet';
            } else if (this.format === 'pdf') {
                return 'PDF';
            } else if (this.format === 'doc') {
                return 'Google Doc';
            } else {
                return '';
            }
        },
    },
    methods: {
        show() {
            this.$refs.modal.show();
            $(this.$refs.cols).sortable();
            // this.print();
        },
        print() {
            const docFormatRowLimit = 250;

            if (this.format !== 'sheet' && this.totalRows > docFormatRowLimit) {
                Confirm.question({
                    title: 'Confirm format',
                    message: `
                        It is recommended to use Google sheet format when exporting
                        tables with more than ${docFormatRowLimit} rows.
                    `,
                    confirmButtonText: 'Continue',
                    cancelButtonText: 'Cancel',
                }).then(({ isConfirmed }) => {
                    if (isConfirmed) {
                        this.sendPrintRequest();
                    }
                });
            } else {
                this.sendPrintRequest();
            }
        },
        getPrintableCols() {
            return $(this.$refs.cols)
                .children('li')
                .elements()
                .map(el => el.data('name'))
                .filter(name => this.cols.find(x => x.name === name).checked);
        },
        sendPrintRequest() {
            const button = $(this.$refs.printButton).getInstance();

            this.$http('print', {
                cols: this.getPrintableCols(),
                format: this.format,
                orientation: this.orientation,
                hidden: true,
            })
                .withButton(button)
                .send()
                .then(({ url }) => {
                    this.$refs.modal.hide();

                    Message.success({
                        title: 'Export complete',
                        message: `
                                <center>
                                    <p>Click the button below to view the printed contents</p>
                                    <p>
                                        <a style="color: black" target="_blank" href="${url}">
                                            <button type="button" class="btn btn-default btn-lg">Open File</button>
                                        </a>
                                    </p>
                                </center>
                            `,
                        allowOutsideClick: false,
                        showCloseButton: true,
                        showConfirmButton: false,
                    });
                });

            const sizeLimitWarning = 250;

            if (this.format !== 'sheet' && this.totalRows > sizeLimitWarning) {
                Message.info(`
                    Your export file is being processed. This might take several minutes 
                    or significantly longer depending on the numbers of rows.
                `);
            }
        },
    },
};
</script>

<style lang="scss" module></style>
<style
    lang="scss"
    module="theme_default"
    src="./../css/themes/default/print_menu.scss"
></style>
<style
    lang="scss"
    module="theme_simple"
    src="./../css/themes/simple/print_menu.scss"
></style>
